import React, { lazy, Suspense, useEffect } from 'react';
import {
  Routes,
  Route,
  Navigate,
  useLocation,
  useRoutes,
} from 'react-router-dom';
import Loading from 'components/shared-components/Loading';
import ComponentErrorBoundary from 'components/shared-components/ComponentErrorBoundary';
import { APP_PREFIX_PATH } from 'configs/AppConfig';
import AuthService from 'services/AuthService';
import { getUserProfile } from 'redux/actions/Auth';
import { useDispatch, useSelector } from 'react-redux';
import Utils from 'utils';
import privilegeMap from 'configs/AccessControlConfig';

export const AppViews = () => {
  const location = useLocation(); // Get the current route
  const dispatch = useDispatch(); // Get the dispatch function from Redux store
  const { token } = useSelector((state) => state.auth);
  const [profileFetched, setProfileFetched] = React.useState(false);

  // Fetch user profile only once when component mounts with a token
  useEffect(() => {
    if (token && !profileFetched) {
      dispatch(getUserProfile());
      setProfileFetched(true);
    }
  }, [token, profileFetched, dispatch]);

  // Verify auth status on route change, but debounce to prevent rapid calls
  useEffect(() => {
    if (!token) return;

    const timeoutId = setTimeout(() => {
      AuthService.verifyStatus().catch(() => {
        // Error is handled by FetchInterceptor
      });
    }, 300); // Debounce by 300ms

    return () => clearTimeout(timeoutId);
  }, [location.pathname, token]);

  const renderWaferSilverCoinRoute = () => {
    return (
      Utils.checkUserPrivileges('Manage 9999 Wafer & Silver Coin') && (
        <Route
          path="999-wafer-silver-coin"
          element={
            <ComponentErrorBoundary
              title="Wafer & Silver Coin Price Error"
              message="Unable to load wafer and silver coin price data."
            >
              {React.createElement(
                lazy(() => import(`views/app-views/wafer-silver-coin-price`)),
              )}
            </ComponentErrorBoundary>
          }
        />
      )
    );
  };

  const renderRetailGoldPriceRoute = () => {
    return (
      Utils.checkUserPrivileges('Manage Retail Gold Price') && (
        <Route
          path="retail-gold-price"
          element={
            <ComponentErrorBoundary
              title="Retail Gold Price Error"
              message="Unable to load retail gold price data."
            >
              {React.createElement(
                lazy(() => import(`views/app-views/retail-gold-price`)),
              )}
            </ComponentErrorBoundary>
          }
        />
      )
    );
  };

  const renderManualCutOffRoute = () => {
    return (
      Utils.checkUserPrivileges('Manage Manual Cut Off') && (
        <Route
          path="manual-cut-off"
          element={
            <ComponentErrorBoundary
              title="Manual Cut Off Error"
              message="Unable to load manual cut off management."
            >
              {React.createElement(
                lazy(() => import(`views/app-views/manual-cut-off`)),
              )}
            </ComponentErrorBoundary>
          }
        />
      )
    );
  };

  const renderManualOverrideRoute = () => {
    return (
      Utils.checkUserPrivileges('Manage Manual Price Override') && (
        <Route
          path="manual-override"
          element={
            <ComponentErrorBoundary
              title="Manual Override Error"
              message="Unable to load manual price override."
            >
              {React.createElement(
                lazy(() => import(`views/app-views/manual-override`)),
              )}
            </ComponentErrorBoundary>
          }
        />
      )
    );
  };

  const renderFeedSetupRoute = () => {
    return (
      Utils.checkUserPrivileges('Manage Circuit Breaker Setup') && (
        <Route
          path="setups/feed-setup"
          element={React.createElement(
            lazy(() => import(`views/app-views/setups/feed-setup`)),
          )}
        />
      )
    );
  };

  const renderTomeiPriceSetupRoute = () => {
    return (
      Utils.checkUserPrivileges('Manage Tomei Price Setup') && (
        <Route
          path="setups/tomei-price-setup"
          element={React.createElement(
            lazy(() => import(`views/app-views/setups/tomei-price-setup`)),
          )}
        />
      )
    );
  };

  const renderFeedPriceSummaryRoute = () => {
    return (
      Utils.checkUserPrivileges('View Feed Price History') && (
        <Route
          path="reports/feed-price-summary"
          element={React.createElement(
            lazy(() => import(`views/app-views/reports/feed-price-summary`)),
          )}
        />
      )
    );
  };

  const renderFeedPriceHistoryRoute = () => {
    return (
      Utils.checkUserPrivileges('View Feed Price History') && (
        <Route
          path="reports/feed-price-history"
          element={React.createElement(
            lazy(() => import(`views/app-views/reports/feed-price-history`)),
          )}
        />
      )
    );
  };

  const renderFeedPriceSetupHistoryRoute = () => {
    return (
      Utils.checkUserPrivileges('View Circuit Breaker Setup History') && (
        <Route
          path="reports/feed-price-setup-history"
          element={React.createElement(
            lazy(
              () => import(`views/app-views/reports/feed-price-setup-history`),
            ),
          )}
        />
      )
    );
  };

  const renderManualPriceActivationHistoryRoute = () => {
    return (
      Utils.checkUserPrivileges('View Manual Price Activation History') && (
        <Route
          path="reports/manual-price-activation-history"
          element={React.createElement(
            lazy(() => import(`views/app-views/reports/manual-price-history`)),
          )}
        />
      )
    );
  };

  const renderCutOffHistoryRoute = () => {
    return (
      Utils.checkUserPrivileges('View Cut Off History') && (
        <Route
          path="reports/cut-off-history"
          element={React.createElement(
            lazy(() => import(`views/app-views/reports/cut-off-history`)),
          )}
        />
      )
    );
  };

  const renderTomeiPriceSummaryRoute = () => {
    return (
      Utils.checkUserPrivileges('View Tomei Price History') && (
        <Route
          path="reports/tomei-price-summary"
          element={React.createElement(
            lazy(() => import(`views/app-views/reports/tomei-price-summary`)),
          )}
        />
      )
    );
  };

  const renderTomeiPriceHistoryRoute = () => {
    return (
      Utils.checkUserPrivileges('View Tomei Price History') && (
        <Route
          path="reports/tomei-price-history"
          element={React.createElement(
            lazy(() => import(`views/app-views/reports/tomei-price-history`)),
          )}
        />
      )
    );
  };

  const renderActionHistoryRoute = () => {
    return (
      Utils.checkUserPrivileges('View Action History') && (
        <Route
          path="action-history"
          element={React.createElement(
            lazy(() => import(`views/app-views/action-history`)),
          )}
        />
      )
    );
  };

  const renderApplicationLogRoute = () => {
    return (
      <Route
        path="application-log"
        element={
          <ComponentErrorBoundary
            title="Application Log Error"
            message="Unable to load application logs."
          >
            {React.createElement(
              lazy(() => import(`views/app-views/application-log`)),
            )}
          </ComponentErrorBoundary>
        }
      />
    );
  };

  const renderDashboardRoute = () => {
    return (
      <Route
        path="dashboard"
        element={
          <ComponentErrorBoundary
            title="Dashboard Error"
            message="Unable to load the dashboard. Please refresh the page or contact support."
          >
            {React.createElement(
              lazy(() => import(`views/app-views/dashboard`)),
            )}
          </ComponentErrorBoundary>
        }
      />
    );
  };

  return (
    <Suspense fallback={<Loading cover="content" />}>
      <Routes>
        {renderDashboardRoute()}
        {renderWaferSilverCoinRoute()}
        {renderRetailGoldPriceRoute()}
        {renderManualCutOffRoute()}
        {renderManualOverrideRoute()}
        {renderFeedSetupRoute()}
        {renderTomeiPriceSetupRoute()}
        {renderFeedPriceSummaryRoute()}
        {renderFeedPriceHistoryRoute()}
        {renderFeedPriceSetupHistoryRoute()}
        {renderManualPriceActivationHistoryRoute()}
        {renderCutOffHistoryRoute()}
        {renderTomeiPriceSummaryRoute()}
        {renderTomeiPriceHistoryRoute()}
        {renderApplicationLogRoute()}
        {renderActionHistoryRoute()}
        <Route path="*" element={<Navigate to="dashboard" replace />} />
      </Routes>
    </Suspense>
  );
};

export default AppViews;
